import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import AuthLeftPanel from '../Common/AuthLeftPanel';
import BackBtn from 'components/Common/BackBtn';
import { setPreviewsRole, setUserInfo } from 'store/auth';
import OTPInput from 'react-otp-input';
import useTranslate from 'hooks/useTranslate';
import { useSendTFAQuery, useVerify2faOtpMutation } from 'services';
import toast from 'react-hot-toast';
import useUserStore from 'store/user';
import { useNavigate } from 'react-router-dom';
import { USER_TYPES } from 'constants';
import { getBrowserID } from 'utils';
import { isMobile } from 'react-device-detect';

const TwoFactorAuthentication: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const [error, setError] = useState<string | null>(null);
	const [otp, setOtp] = useState('');
	const [timer, setTimer] = useState(30);
	const [isTimerRunning, setIsTimerRunning] = useState(false);
	const { tempLoginData }: any = useUserStore((state) => state);

	// mutations
	const payload = { userId: tempLoginData?.user?.userId, userType: tempLoginData?.user?.userType };
	const { refetch } = useSendTFAQuery(payload);
	const { mutateAsync: verify2faOTPMutation } = useVerify2faOtpMutation();

	const handleSubmit = async (e: any) => {
		e.preventDefault();
		if (otp.length !== 4) {
			setError('Please enter otp');
			return;
		}
		setError(null);

		verify2faOTPMutation({
			otp: otp,
			userId: tempLoginData?.user?.userId,
			browserToken: getBrowserID(),
		}).then((res: any) => {
			if (res?.success) {
				toast.success(res?.message);
				setUserInfo(tempLoginData);
				setPreviewsRole(tempLoginData?.user?.userType);
				if (tempLoginData?.user?.userType === USER_TYPES.COMPANY) {
					navigate(`/company/profile`);
					return;
				} else {
					if (tempLoginData?.user?.preference) {
						navigate(`/seeker/profile`);
						return;
					} else {
						navigate(`/seeker/preference`);
						return;
					}
				}
			}
		});
	};

	const handleRequestOtp = async () => {
		refetch();
		setTimer(30);
		setIsTimerRunning(true);
	};

	useEffect(() => {
		const keyDownHandler = (event: any) => {
			if (event.key === 'Enter') {
				handleSubmit(event);
			}
		};

		document.addEventListener('keydown', keyDownHandler);
		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
	}, [otp]);

	useEffect(() => {
		let countdown: any = null;

		if (isTimerRunning) {
			countdown = setInterval(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
		}

		return () => clearInterval(countdown);
	}, [isTimerRunning]);

	useEffect(() => {
		if (timer === 0) {
			setIsTimerRunning(false);
		}
	}, [timer]);

	return (
		<div className="auth bg-white d-flex flex-column flex-lg-row">
			<div className="auth-left-content">
				<AuthLeftPanel />
			</div>
			<div className="auth-right-content flex-1 d-flex align-items-center justify-content-center position-relative">
				<BackBtn />
				<div className="auth-form mb-0 mt-5 mx-3 my-5 m-md-5">
					<div className="auth-form-heading text-center">
						<h2 className="mb-3">
							{translate('Common.twoFactor')}{' '}
							<span className="fw-bold">
								<br></br>
								{translate('Auth.authentication')}
							</span>
						</h2>
						<p className="mb-0">{translate('Common.typeOTP')}</p>
					</div>
					<div className="auth-form-fields">
						<Form className="form" onSubmit={handleSubmit}>
							<div className="otp-box position-relative">
								<OTPInput
									value={otp}
									onChange={setOtp}
									numInputs={4}
									renderSeparator={<span>-</span>}
									renderInput={(props) => <input {...props} />}
									inputType={isMobile ? 'number' : 'text'}
								/>
								{error && <div className="f-error text-danger">{error}</div>}
							</div>
							<div className="form-btn">
								<Button className="filled-btn rounded-pill w-100" type="submit">
									{translate('Common.submit')}
								</Button>
							</div>
							<p className="mb-0 text-center">
								{translate('Common.didNotReceiveOtp')}{' '}
								<Button
									variant="link"
									onClick={() => {
										if (!isTimerRunning) {
											setIsTimerRunning(true);
										}
										handleRequestOtp();
									}}
									disabled={isTimerRunning}
									className="fw-bold join-link p-0 border-0"
								>
									{isTimerRunning
										? `${translate('Modal.waitFor')} ${timer}  ${translate('Modal.seconds')}`
										: translate('Modal.resend')}
								</Button>
							</p>
						</Form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TwoFactorAuthentication;
